import { Send } from 'iconoir-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/shared';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import { PrimitiveButton } from '@savgroup-front-common/primitive/ui/button';
import { Separator } from '@savgroup-front-common/primitive/ui/separator';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';

import { PublicBar } from '../PublicBar/PublicBar';

import messages from './messages';
import useResendSalesLeadEmailConfirmationCommand from './OnboardingConfirmation.hooks';

const OnboardingConfirmation = ({ userEmail }: { userEmail: string }) => {
  const navigate = useNavigate();
  const { handleResendEmail, isLoading } =
    useResendSalesLeadEmailConfirmationCommand({ userEmail });

  useEffect(() => {
    if (!userEmail || userEmail.length === 0) {
      navigate('/onboarding', { replace: true });
    }
  }, [userEmail, navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-surface-primary-subtle-default">
      <PublicBar />
      <main className="flex-1 px-4 py-8 md:px-8 md:py-16">
        <div
          className={cn(
            'mx-auto flex max-w-335 flex-col items-center rounded-xl bg-surface-base-default',
            'px-5 py-8 text-center md:px-24 md:py-9',
          )}
        >
          <Send aria-hidden className="size-6 stext-primary-default" />
          <h1 className="mt-3.5 mb-8 text-2xl font-bold stext-base-default">
            <SafeFormattedMessageWithoutSpread
              message={messages.accountActivationTitle}
            />
          </h1>
          <p className="text-base leading-relaxed stext-muted-default">
            <SafeFormattedMessageWithoutSpread
              message={messages.verification}
            />
          </p>
          <p className="mt-5 text-base leading-relaxed font-semibold stext-base-default">
            <SafeFormattedMessageWithoutSpread
              message={messages.emailActivation}
              values={{
                email: (
                  <span className="break-all stext-primary-default">
                    {userEmail}
                  </span>
                ),
              }}
            />
          </p>
          <Separator className="my-8" />
          <p className="mb-5 text-sm stext-muted-default">
            <SafeFormattedMessageWithoutSpread message={messages.spam} />
          </p>
          <PrimitiveButton
            type={BUTTON_TYPES.BUTTON}
            size="lg"
            className="px-5"
            disabled={isLoading}
            onClick={() => handleResendEmail()}
            data-testid="OnboardingConfirmation-resend"
          >
            {isLoading && <Spinner />}
            <SafeFormattedMessageWithoutSpread message={messages.resend} />
          </PrimitiveButton>
        </div>
      </main>
    </div>
  );
};

OnboardingConfirmation.displayName = 'OnboardingConfirmation';

export default OnboardingConfirmation;
